import { FC, useState } from 'react';
import { ProactiveInsight } from '../../AIChat/hooks/useProactiveInsights';
import InsightBanner from '../../AIChat/components/InsightBanner';
import { V2_SUGGESTED_QUESTIONS, V2_WELCOME_MESSAGES } from '../constants';
import { TIER_DISPLAY_NAMES, PortfolioTier } from '../services/tierConfig';

interface WelcomeScreenProps {
  tier: PortfolioTier;
  contextLoading: boolean;
  insights: ProactiveInsight[];
  onDismissInsight: (id: string) => void;
  onSendMessage: (content: string) => void;
}

const WelcomeScreen: FC<WelcomeScreenProps> = ({ tier, contextLoading, insights, onDismissInsight, onSendMessage }) => {
  const [welcomeMessage] = useState(() => V2_WELCOME_MESSAGES[Math.floor(Math.random() * V2_WELCOME_MESSAGES.length)]);

  return (
    <div className="flex-1 overflow-y-auto px-4 py-8">
      <div className="max-w-xl w-full mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-vine dark:bg-teal-600 flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
          </div>

          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">Portfolio Assistant</h2>

          <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-vine/10 dark:bg-teal-500/10 text-vine dark:text-teal-400 mb-4">
            {TIER_DISPLAY_NAMES[tier]}
          </div>

          <p className="text-gray-500 dark:text-gray-400 text-sm">{welcomeMessage}</p>
        </div>

        {/* Proactive insights */}
        {insights.length > 0 && (
          <div className="mb-8">
            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
              Portfolio Alerts
            </h4>
            {insights.slice(0, 3).map((insight) => (
              <InsightBanner key={insight.id} insight={insight} onDismiss={onDismissInsight} />
            ))}
          </div>
        )}

        {/* Context loading indicator */}
        {contextLoading && (
          <div className="flex items-center justify-center gap-2 mb-6 text-sm text-gray-400 dark:text-gray-500">
            <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Loading portfolio context...
          </div>
        )}

        {/* Suggested questions */}
        <div>
          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
            Try asking
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {V2_SUGGESTED_QUESTIONS.map((question, index) => (
              <button
                key={index}
                onClick={() => onSendMessage(question)}
                disabled={contextLoading}
                className="text-left p-4 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 text-sm hover:border-orange dark:hover:border-teal-500 hover:shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {question}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeScreen;
